import { CAR_BY_ID } from '../shared/cars.js';
import type { RacerSnapshot, StyleAction } from '../shared/types.js';
import { vehicleSignatureFor } from '../shared/vehicle-signatures.js';
import type { RaceRunStats } from './progression.js';
import {
  readLocalCareerSection,
  updateLocalCareerSection,
} from './local-career-storage.js';

export interface VehicleMasteryRecord {
  races: number;
  finishes: number;
  wins: number;
  bestStyle: number;
  signatureProgress: number;
}

export interface VehicleMasteryState {
  version: 1;
  awardedRaceIds: string[];
  records: Record<string, VehicleMasteryRecord>;
}

export interface VehicleMasteryGoal {
  id: 'finish' | 'win' | 'signature';
  name: string;
  description: string;
  progress: number;
  target: number;
  complete: boolean;
}

export interface VehicleMasteryUpdate {
  carId: string;
  before: VehicleMasteryRecord;
  after: VehicleMasteryRecord;
  goals: VehicleMasteryGoal[];
  newlyCompleted: VehicleMasteryGoal[];
  paintUnlocked: boolean;
  newlyUnlocked: boolean;
  duplicate: boolean;
}

const STORAGE_KEY = 'nightshift:vehicle-mastery:v1';
const MAX_RACE_IDS = 192;

const MASTERY_PAINTS: Readonly<Record<string, string>> = {
  'vortex-r': '#42f5c5',
  'apex-gt': '#f4c85c',
  'tempest-x': '#ff5f47',
  'nightfall-rs': '#7b8dff',
  'titan-xr': '#d66cff',
  'mammoth-6x6': '#9fc94b',
  'juggernaut-rig': '#e57b38',
  'ironclad-t9': '#c8d2da',
  'volt-r9': '#3ee8ff',
  'nomad-xr': '#f08a58',
  'wiener-wagon': '#b8642a',
  quackster: '#5fd0ff',
  'couch-rocket': '#7b2a3a',
  'bigfoot-bubble': '#2f6df0',
};

export function createVehicleMasteryState(): VehicleMasteryState {
  return { version: 1, awardedRaceIds: [], records: {} };
}

export function parseVehicleMastery(raw: string | null): VehicleMasteryState {
  const fallback = createVehicleMasteryState();
  if (!raw) return fallback;
  try {
    const candidate = JSON.parse(raw) as {
      awardedRaceIds?: unknown;
      records?: unknown;
    };
    const records: Record<string, VehicleMasteryRecord> = {};
    if (candidate.records && typeof candidate.records === 'object') {
      for (const [carId, value] of Object.entries(candidate.records)) {
        if (!CAR_BY_ID.has(carId) || !value || typeof value !== 'object') continue;
        const record = value as Partial<VehicleMasteryRecord>;
        records[carId] = {
          races: integer(record.races),
          finishes: integer(record.finishes),
          wins: integer(record.wins),
          bestStyle: integer(record.bestStyle),
          signatureProgress: integer(record.signatureProgress),
        };
      }
    }
    return {
      version: 1,
      awardedRaceIds: Array.isArray(candidate.awardedRaceIds)
        ? candidate.awardedRaceIds.filter((value): value is string => typeof value === 'string' && value.length <= 96).slice(-MAX_RACE_IDS)
        : [],
      records,
    };
  } catch {
    return fallback;
  }
}

export function loadVehicleMastery(): VehicleMasteryState {
  try {
    const career = readLocalCareerSection('vehicleMastery');
    if (career !== undefined) return parseVehicleMastery(JSON.stringify(career));
    return parseVehicleMastery(localStorage.getItem(STORAGE_KEY));
  } catch {
    return createVehicleMasteryState();
  }
}

export function saveVehicleMastery(state: VehicleMasteryState): void {
  const value = { ...state, version: 1 };
  const envelopeUpdated = updateLocalCareerSection('vehicleMastery', value);
  if (envelopeUpdated !== null) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Vehicle mastery is a local convenience and never blocks a race.
  }
}

export function vehicleMasteryRecord(
  state: Pick<VehicleMasteryState, 'records'>,
  carId: string,
): VehicleMasteryRecord {
  return state.records[carId] ?? {
    races: 0,
    finishes: 0,
    wins: 0,
    bestStyle: 0,
    signatureProgress: 0,
  };
}

export function vehicleMasteryGoals(carId: string, record: VehicleMasteryRecord): VehicleMasteryGoal[] {
  const car = CAR_BY_ID.get(carId);
  const signature = vehicleSignatureFor(car ?? { id: carId });
  const signatureTarget = signature.id === 'overmatch' ? 2 : 4;
  return [
    {
      id: 'finish',
      name: 'SHAKEDOWN',
      description: 'Finish a race with this vehicle.',
      progress: record.finishes,
      target: 1,
      complete: record.finishes >= 1,
    },
    {
      id: 'win',
      name: 'PROVEN',
      description: 'Win against at least one rival.',
      progress: record.wins,
      target: 1,
      complete: record.wins >= 1,
    },
    {
      id: 'signature',
      name: signature.name,
      description: signatureGoalDescription(signature.id),
      progress: record.signatureProgress,
      target: signatureTarget,
      complete: record.signatureProgress >= signatureTarget,
    },
  ];
}

export function masteryPaintForCar(carId: string): string | null {
  return MASTERY_PAINTS[carId] ?? null;
}

export function masteryPaintUnlocked(
  state: Pick<VehicleMasteryState, 'records'>,
  carId: string,
): boolean {
  const goals = vehicleMasteryGoals(carId, vehicleMasteryRecord(state, carId));
  return goals.every((goal) => goal.complete);
}

export function recordVehicleMastery(
  state: VehicleMasteryState,
  racer: RacerSnapshot,
  totalRacers: number,
  run: RaceRunStats,
  raceId: string,
  persist = true,
): { state: VehicleMasteryState; update: VehicleMasteryUpdate } {
  const before = { ...vehicleMasteryRecord(state, racer.carId) };
  const beforeGoals = vehicleMasteryGoals(racer.carId, before);
  const duplicate = Boolean(raceId && state.awardedRaceIds.includes(raceId));
  if (duplicate) {
    return {
      state,
      update: {
        carId: racer.carId,
        before,
        after: before,
        goals: beforeGoals,
        newlyCompleted: [],
        paintUnlocked: beforeGoals.every((goal) => goal.complete),
        newlyUnlocked: false,
        duplicate: true,
      },
    };
  }
  const finished = racer.finishedAt !== null;
  const after: VehicleMasteryRecord = {
    races: before.races + 1,
    finishes: before.finishes + Number(finished),
    wins: before.wins + Number(finished && racer.place === 1 && totalRacers >= 2),
    bestStyle: Math.max(before.bestStyle, Math.round(racer.styleScore)),
    signatureProgress: before.signatureProgress + signatureProgressForRun(racer.carId, run),
  };
  const goals = vehicleMasteryGoals(racer.carId, after);
  const completedBefore = new Set(beforeGoals.filter((goal) => goal.complete).map((goal) => goal.id));
  const newlyCompleted = goals.filter((goal) => goal.complete && !completedBefore.has(goal.id));
  const paintUnlocked = goals.every((goal) => goal.complete);
  const next: VehicleMasteryState = {
    version: 1,
    awardedRaceIds: raceId ? [...state.awardedRaceIds, raceId].slice(-MAX_RACE_IDS) : state.awardedRaceIds,
    records: { ...state.records, [racer.carId]: after },
  };
  if (persist) saveVehicleMastery(next);
  return {
    state: next,
    update: {
      carId: racer.carId,
      before,
      after,
      goals,
      newlyCompleted,
      paintUnlocked,
      newlyUnlocked: paintUnlocked && !beforeGoals.every((goal) => goal.complete),
      duplicate: false,
    },
  };
}

function signatureProgressForRun(carId: string, run: RaceRunStats): number {
  const signature = vehicleSignatureFor(CAR_BY_ID.get(carId) ?? { id: carId });
  const action = (name: StyleAction): number => run.actions[name] ?? 0;
  switch (signature.id) {
    case 'long-burn':
      return action('SLINGSHOT') + action('PERFECT LAUNCH');
    case 'apex-hunter':
      return run.perfectCorners;
    case 'precision-chain':
      return run.perfectCorners + action('CLEAN PASS');
    case 'smoke-show':
      return action('DRIFT BOOST');
    case 'trailblazer':
      return run.routes;
    case 'road-presence':
      return action('CLEAN PASS') + action('TAKEDOWN');
    case 'overmatch':
      return action('TAKEDOWN');
    case 'air-attack':
      return action('AIR');
    case 'wild-line':
      return run.routes + action('AIR');
    default:
      return run.maxCombo >= 6 ? 1 : 0;
  }
}

function signatureGoalDescription(signatureId: string): string {
  switch (signatureId) {
    case 'long-burn': return 'Land four perfect launches or slingshots.';
    case 'apex-hunter': return 'Take four perfect corners.';
    case 'precision-chain': return 'Bank four perfect corners or clean passes.';
    case 'smoke-show': return 'Catch four drifts for a drift boost.';
    case 'trailblazer': return 'Complete four alternate routes.';
    case 'road-presence': return 'Land four clean passes or takedowns.';
    case 'overmatch': return 'Score two takedowns.';
    case 'air-attack': return 'Land four scored jumps.';
    case 'wild-line': return 'Bank four routes or scored jumps.';
    default: return 'Build a six-action style chain four times.';
  }
}

function integer(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(1_000_000_000, Math.floor(parsed))) : 0;
}
